import React, { useState } from "react";
import { Box, Button, Typography, Stack, Alert, CircularProgress } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import { baseURL } from "services/API";
import axios from "axios";
import { useSelector } from "react-redux";

const BulkPaymentsUpload = ({ rentalId }) => {
  const [file, setFile] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const token = useSelector((state) => state.token);

  const handleDownloadTemplate = async () => {
    if (!rentalId) {
      setError("Missing property id.");
      return;
    }
    setError(null);
    setMessage(null);
    setIsDownloading(true);
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${baseURL}/tenants/bulk/download-template?rental_id=${rentalId}`, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rentbeta_bulk_upload_template_${rentalId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage("Template downloaded.");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setMessage(null);
    setError(null);
    setSummary(null);
  };

  const handleUpload = async () => {
    if (!rentalId) {
      setError("Missing property id.");
      return;
    }
    if (!file) {
      setError("Please choose an Excel file (.xlsx/.xls).");
      return;
    }
    setIsUploading(true);
    setError(null);
    setMessage(null);
    setSummary(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const res = await axios.post(`${baseURL}/tenants/bulk/upload?rental_id=${rentalId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSummary(res.data);
      setMessage("Upload processed successfully.");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || e.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Bulk upload tenants and payments</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Download the template, fill headers: NAME, UNIT No, CONTACT, RENT AMOUNT, PAID, BAL, DUE DATE, PAY'T DATE, PAY'T MD. Then upload the filled file.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={isDownloading ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
          onClick={handleDownloadTemplate}
          disabled={isDownloading}
        >
          Download template
        </Button>

        <Button
          variant="contained"
          component="label"
          startIcon={<DescriptionIcon />}
        >
          Choose file
          <input hidden type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
        </Button>
        {file && (
          <Typography variant="body2">{file.name}</Typography>
        )}

        <Button
          variant="contained"
          color="success"
          startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          onClick={handleUpload}
          disabled={isUploading || !file}
        >
          Upload
        </Button>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Summary</Typography>
          <pre style={{
            background: "#f7f7f7",
            borderRadius: 4,
            padding: 12,
            overflow: "auto",
            maxHeight: 360
          }}>{JSON.stringify(summary, null, 2)}</pre>
        </Box>
      )}
    </Box>
  );
};

export default BulkPaymentsUpload;


